import { useState } from 'react';
import type { Recurso } from '../../api/ops/pagos';
import type { ReservaEnVivo } from '../../api/ops/sala';
import { Camara } from '../../tablet/Camara';
import { Campo } from '../../componentes/Campo';
import { Contador } from '../../componentes/Contador';
import { Desplegable } from '../../componentes/Desplegable';
import { Icono } from '../../componentes/Icono';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Ventana } from '../../componentes/Ventana';
import { mayuscula } from '../reservas/textos';
import { horaEn } from '../reservas/fechas';
import { horasDeReten } from './logica';

/**
 * Las ventanas de la Sala (8.7): walk-in (G399), retén de puerta (G462), escanear, desplazar (G397, sin aviso automático,
 * G398), «sentar igualmente» (G16) y forzar la capacidad con motivo. Solo piden datos; quien las abre llama a la API.
 */
interface PropsWalkIn {
    abierto: boolean;
    movil: boolean;
    palabra: string;
    subtitulo: string;
    libresPara: (personas: number) => Recurso[];
    nombreZona: (zonaId: string) => string;
    inicial: string | null;
    ocupado: boolean;
    onCerrar: () => void;
    onSentar: (datos: { personas: number; telefono: string; recurso: string }) => void;
}

/** Walk-in: personas, teléfono si lo da y el recurso libre donde se sienta (G399). */
export function WalkIn({ abierto, movil, palabra, subtitulo, libresPara, nombreZona, inicial, ocupado, onCerrar, onSentar }: PropsWalkIn) {
    const [personas, setPersonas] = useState(2);
    const [telefono, setTelefono] = useState('');
    const [elegido, setElegido] = useState<string | null>(inicial);
    const libres = libresPara(personas);
    const recurso = elegido !== null && libres.some((r) => r.id === elegido) ? elegido : null;
    return (
        <PanelLateral
            abierto={abierto}
            onCerrar={onCerrar}
            movil={movil}
            icono="user-plus"
            eyebrow="Sin reserva"
            titulo="Walk-in"
            subtitulo={subtitulo}
            accion="Sentar"
            accionIcono="armchair"
            ocupado={ocupado}
            ocupadoTexto="Sentando…"
            inactiva={recurso === null}
            onAccion={() => {
                if (recurso !== null) {
                    onSentar({ personas, telefono: telefono.trim(), recurso });
                }
            }}
        >
            <div className="rv-sl-form">
                <Contador etiqueta="Personas" ayuda="Cuántas se sientan" valor={personas} onCambio={setPersonas} />
                <Campo etiqueta="Teléfono (si lo da)" tipo="tel" autocompletar="off" valor={telefono} onChange={setTelefono} ayuda="Sin teléfono queda como cliente anónimo." />
                <p className="rv-sl-ceja">{`${mayuscula(palabra)}s libres para ${String(personas)}`.toLocaleUpperCase('es')}</p>
                {libres.length === 0 ? (
                    <p className="rv-sl-nota">{`No hay ningún ${palabra} libre para ${String(personas)}. Prueba con menos personas o junta dos en el plano.`}</p>
                ) : (
                    <div className="rv-sl-libres" role="radiogroup" aria-label={`${mayuscula(palabra)}s libres`}>
                        {libres.map((r) => (
                            <button
                                key={r.id}
                                type="button"
                                role="radio"
                                aria-checked={recurso === r.id}
                                className={`rv-sl-libre${recurso === r.id ? ' rv-sl-libre--elegido' : ''}`}
                                onClick={() => { setElegido(r.id); }}
                            >
                                <strong>{`${mayuscula(palabra)} ${r.code ?? ''}`}</strong>
                                <small>{`${nombreZona(r.current_terms.zone_id)} • ${String(r.current_terms.min_capacity)}–${String(r.current_terms.max_capacity)}`}</small>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </PanelLateral>
    );
}

/** Retén de puerta: hasta qué hora y el motivo (G462). */
export function VentanaReten({ abierta, codigo, zona, ahora, ocupado, error, onCerrar, onGuardar }: { abierta: boolean; codigo: string; zona: string; ahora: Date; ocupado: boolean; error: string | null; onCerrar: () => void; onGuardar: (hasta: string, motivo: string) => void }) {
    const horas = horasDeReten(ahora, zona);
    const [hasta, setHasta] = useState(horas[2]?.valor ?? '');
    const [motivo, setMotivo] = useState('');
    return (
        <Ventana
            abierta={abierta}
            titulo={`Guardar ${codigo} para la puerta`}
            texto="La web, el widget y el chat dejan de venderlo hasta esa hora. Aquí sí puedes sentar a alguien en él."
            accion="Guardar para la puerta"
            accionIcono="door-open"
            ocupado={ocupado}
            onAccion={() => { onGuardar(hasta, motivo.trim()); }}
            onCerrar={onCerrar}
        >
            <Desplegable etiqueta="Hasta" valor={hasta} opciones={horas.map((h) => ({ valor: h.valor, texto: h.texto }))} onChange={setHasta} ayuda="Como mucho, hasta el final de la jornada." error={error} />
            <Campo etiqueta="Motivo (opcional)" valor={motivo} onChange={setMotivo} max={140} />
        </Ventana>
    );
}

/** Escanear: la cámara (la de la Puerta, 6.1) o el código del pase tecleado. */
export function VentanaEscanear({ abierta, ocupado, resultado, onCerrar, onLeido, onCodigo }: { abierta: boolean; ocupado: boolean; resultado: { bien: boolean; texto: string } | null; onCerrar: () => void; onLeido: (qr: string) => void; onCodigo: (codigo: string) => void }) {
    const [codigo, setCodigo] = useState('');
    return (
        <Ventana
            abierta={abierta}
            titulo="Escanear el pase"
            texto="Apunta al QR del pase o escribe su código. La llegada queda registrada al momento."
            accion="Registrar la llegada"
            accionIcono="scan-line"
            ocupado={ocupado}
            inactiva={codigo.trim() === ''}
            onAccion={() => { onCodigo(codigo.trim().toUpperCase()); }}
            onCerrar={onCerrar}
        >
            <Camara abierta={abierta} onLeido={onLeido} />
            <Campo etiqueta="Código del pase" valor={codigo} onChange={setCodigo} mayusculas="characters" autocompletar="off" onEnter={() => { if (codigo.trim() !== '') { onCodigo(codigo.trim().toUpperCase()); } }} />
            {resultado !== null && (
                <p className={`rv-sl-resultado${resultado.bien ? '' : ' rv-sl-resultado--mal'}`} role="status">
                    <Icono nombre={resultado.bien ? 'check' : 'triangle-alert'} />
                    <span>{resultado.texto}</span>
                </p>
            )}
        </Ventana>
    );
}

export type Confirmacion =
    | { tipo: 'desplazar'; codigos: string; desplazadas: readonly ReservaEnVivo[]; nombres: Readonly<Record<string, string>>; zona: string }
    | { tipo: 'igualmente'; texto: string }
    | { tipo: 'capacidad'; codigos: string };

/** Desplazar (G397), «sentar igualmente» (G16) o forzar la capacidad (con motivo, queda en Auditoría). */
export function VentanaConfirmar({ confirmacion, sentar, ocupado, onCerrar, onConfirmar }: { confirmacion: Confirmacion | null; sentar: boolean; ocupado: boolean; onCerrar: () => void; onConfirmar: (motivo: string) => void }) {
    const [motivo, setMotivo] = useState('');
    const c = confirmacion;
    if (c === null) {
        return null;
    }
    if (c.tipo === 'desplazar') {
        const nombres = c.desplazadas.map((r) => (r.customer_id == null ? 'Sin reserva' : (c.nombres[r.customer_id] ?? 'Cliente')));
        return (
            <Ventana
                abierta
                titulo={`Quitar ${c.codigos} a ${nombres.join(' y ')}`}
                texto="Se queda sin sitio y pasa a «Para recolocar». No se le avisa: decide tú si le llamas."
                accion={sentar ? 'Desplazar y sentar' : 'Desplazar y asignar'}
                accionIcono="arrow-right"
                ocupado={ocupado}
                onAccion={() => { onConfirmar(''); }}
                onCerrar={onCerrar}
            >
                <ul className="rv-sl-desplazadas">
                    {c.desplazadas.map((r, i) => (
                        <li key={r.reservation_id}>
                            <span>{horaEn(r.starts_at, c.zona)}</span>
                            <strong>{`${nombres[i] ?? ''} • ${String(r.party_size)} pers.`}</strong>
                        </li>
                    ))}
                </ul>
            </Ventana>
        );
    }
    if (c.tipo === 'igualmente') {
        return (
            <Ventana abierta titulo="No es lo que reservó" texto={c.texto} accion="Sentar igualmente" accionIcono="armchair" ocupado={ocupado} onAccion={() => { onConfirmar(''); }} onCerrar={onCerrar} />
        );
    }
    return (
        <Ventana
            abierta
            titulo="No cabe"
            texto={`${c.codigos} no tiene sitio para este grupo o está retenido para la puerta. Puedes sentarlos igualmente: queda registrado con el motivo.`}
            accion="Forzar y sentar"
            accionIcono="triangle-alert"
            ocupado={ocupado}
            inactiva={motivo.trim() === ''}
            onAccion={() => { onConfirmar(motivo.trim()); }}
            onCerrar={onCerrar}
        >
            <Campo etiqueta="Motivo" valor={motivo} onChange={setMotivo} max={500} />
        </Ventana>
    );
}
